import { createContext, useContext, useCallback } from "react";
import { getIdToken } from "firebase/auth";
import { useUser } from "auth";
import { useArtistContext } from "Components";
import { useExceptionLogging } from "hooks/useExceptionLogging";
import type { Client } from "typesense";

const MAX_RETRIES = 3;
const RETRY_DELAY = 1500;

type InferReturnFromArgs<
  F extends (...args: any) => any,
  Args extends any[],
> = F extends (...args: Args) => infer R ? R : never;

type InferredCollection = InferReturnFromArgs<Client["collections"], [string]>;

// Extract the first (zero-arg) overload of documents() using a more explicit pattern
type ExtractDocumentsOverload<T> = T extends {
  documents(): infer R;
  documents(id: string): any;
}
  ? R
  : T extends { documents(): infer R }
  ? R
  : never;

// Get the Documents<T> type (plural, has search method)
type InferredDocuments = ExtractDocumentsOverload<InferredCollection>;

// Finally extract SearchParams from the search method
export type SearchParams = Parameters<InferredDocuments["search"]>[0];

// TODO: Switch all app typings to above
export interface ProxySearchParams {
  q: string;
  query_by?: string | string[];
  filter_by?: string;
  sort_by?: string | string[];
  facet_by?: string | string[];
  max_facet_values?: number;
  page?: number;
  per_page?: number;
  group_by?: string | string[];
  offset?: number;
  limit?: number;
  include_fields?: string;
}

export type ProxyCollection =
  | "lead_sources"
  | "contacts"
  | "setlive_events"
  | "surveys"
  | "submissions"
  | "artist_groups"
  | "brands"
  | "categories";

interface TypsenseProxyProps {
  proxySearch: (
    collection: ProxyCollection,
    params: SearchParams,
    logQueryMetadata?: boolean,
  ) => Promise<Record<string, any>>;
}

export const sanitizeTypesenseParams = (
  params: SearchParams,
): Record<string, string> => {
  const p: Record<string, string> = {};
  for (const key of Object.keys(params)) {
    if (typeof params[key] === "number") p[key] = `${params[key]}`;
    else p[key] = params[key];
  }
  return p;
};

export const TypesenseProxyContext = createContext<TypsenseProxyProps>(null);

export const TypesenseProxyProvider = ({ children }) => {
  const { user } = useUser();
  // this might be called from inside gtb, meaning we won't have an artist context to
  // pull from. shortcircuiting to ensure we don't break
  const { id: artistGroupId, logAction } = useArtistContext() || {
    id: undefined,
    logAction: () => {},
  };
  const { logException } = useExceptionLogging();

  const proxySearch = useCallback(
    async (
      collection: ProxyCollection,
      params: SearchParams,
      logQueryMetadata = false,
    ): Promise<Record<string, any>> => {
      if (!user) {
        console.log("No user user for proxy");
        logException(new Error("No user for proxy"), {
          service: "typesense",
        });
        return;
      }

      const token = await getIdToken(user);

      if (artistGroupId && logQueryMetadata) {
        await logAction("portal_query", { collection, params });
      }

      const url = new URL(
        `/api/search/collections/${collection}/documents/search`,
        window.location.origin,
      );

      url.search = new URLSearchParams(
        sanitizeTypesenseParams(params),
      ).toString();

      let attempts = 0;
      let status = 200;

      while (attempts < MAX_RETRIES) {
        try {
          const response = await fetch(url.toString(), {
            headers: {
              authorization: `bearer ${token}`,
            },
          });

          if (!response.ok) {
            status = response.status;
            throw new Error(await response.text());
          }

          return await response.json();
        } catch (error) {
          attempts++;

          if (attempts < MAX_RETRIES) {
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
          } else {
            logException(error, {
              service: "typesense",
              collection,
              status,
            });

            throw error;
          }
        }
      }
    },
    [user],
  );

  return (
    <TypesenseProxyContext.Provider
      value={{
        proxySearch,
      }}
    >
      {children}
    </TypesenseProxyContext.Provider>
  );
};

export const useTypesenseProxyContext = () => useContext(TypesenseProxyContext);
